import { useState, useEffect } from 'react'
import { authService } from '@/api/auth'
import { Card, Tag, Alert, Title, Text, Table, Icon } from '@nimbus-ds/components'
import { D3SankeyChart } from './D3SankeyChart'
import { visitorsOf, weightedAvgConversion, stepNonCompleters } from './productInsights'
import { TrafficSourceTable } from '@/components/analytics/TrafficSourceTable'
import { TrafficSourceTimeline } from '@/components/analytics/TrafficSourceTimeline'
import { BreakdownTable } from '@/components/analytics/BreakdownTable'
import { OffStorefrontCard } from '@/components/analytics/OffStorefrontCard'
import { SearchAnalyticsCard } from '@/components/analytics/SearchAnalyticsCard'
import { RevenueAnalyticsCard } from '@/components/analytics/RevenueAnalyticsCard'
import { RevenueTrendChart } from '@/components/analytics/RevenueTrendChart'
import { ProductPerformanceTable } from '@/components/analytics/ProductPerformanceTable'
import { ProductPerformanceTimeline } from '@/components/analytics/ProductPerformanceTimeline'
import { OptimizationOpportunities } from '@/components/analytics/OptimizationOpportunities'
import { VisitorSegmentation } from '@/components/analytics/VisitorSegmentation'
import { useDashboardVersion } from '@/lib/featureFlags'
import {
  ArrowDownIcon,
  ArrowupIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  StatsIcon,
  FlagIcon,
  CheckCircleIcon,
} from '@nimbus-ds/icons'
import type { Experiment, FunnelConfig } from '@/types'
import { funnelService, type ProductsOriginsTimelineResponse } from '@/api/funnel'

interface FunnelAnalyticsProps {
  experiments: Experiment[]
  selectedExperimentId?: string
  onExperimentChange?: (experimentId: string) => void
  /** Pre-fetched funnel data - pass null while loading, undefined to let component fetch itself */
  initialFunnelData?: any | null
  /** Time window in days; controls self-fetch and display label */
  windowDays?: number
}

// Step names will be extracted from the actual funnel configuration
// or use the step ID as fallback

export function FunnelAnalytics({
  // Props kept for backward compatibility but no longer used
  experiments: _experiments,
  selectedExperimentId: _selectedExperimentId,
  onExperimentChange: _onExperimentChange,
  initialFunnelData,
  windowDays = 7,
}: FunnelAnalyticsProps) {
  const [funnelData, setFunnelData] = useState<any | null>(initialFunnelData ?? null)
  const [funnelConfig, setFunnelConfig] = useState<FunnelConfig | null>(null)
  // Loading if: parent is providing data (initialFunnelData !== undefined) but hasn't loaded yet (!initialFunnelData)
  // OR: component will fetch itself (initialFunnelData === undefined)
  const [loading, setLoading] = useState(initialFunnelData === undefined || !initialFunnelData)
  const [timelineData, setTimelineData] = useState<ProductsOriginsTimelineResponse | null>(null)
  const dashboardVersion = useDashboardVersion()

  // Transform data for visualization with multi-entry metrics
  const stepAnalysisData = (funnelData as any)?.tnx_analytics?.step_analysis ||
                          (funnelData as any)?.step_analysis ||
                          (funnelData as any)?.steps || {}

  // Helper: Get actual conversion count from conversion step (is_conversion=true)
  const getConversionCount = () => {
    const conversionStep = Object.values(stepAnalysisData).find((s: any) => s.is_conversion === true) as any
    return conversionStep?.total_sessions || 0
  }


  const chartData = Object.keys(stepAnalysisData).length > 0
    ? Object.entries(stepAnalysisData).map(([stepId, data]: any, index) => {

        // Extract data from the enhanced analytics structure
        const totalSessions = data.total_sessions ?? 0
        const sessionsEntered = data.sessions_entered_here ?? 0
        const conversionsFromEntry = data.conversions_from_entry ?? 0
        const conversionsReached = data.conversions_reached ?? 0
        const totalSessionsOverall = (funnelData as any)?.tnx_analytics?.total_sessions ??
                                   (funnelData as any)?.total_sessions ?? 0

        // Calculate the three key metrics
        // 1. Entry Share: % of total sessions that entered at this step
        const entryShare = totalSessionsOverall > 0
          ? (sessionsEntered / totalSessionsOverall) * 100
          : 0

        // 2. Entry-to-Conversion Rate: % of sessions entering here who eventually convert
        const entryConversionRate = sessionsEntered > 0
          ? (conversionsFromEntry / sessionsEntered) * 100
          : 0

        // 2b. Reach-to-Conversion baseline: of ALL sessions that reached this step,
        // % that ultimately converted. Large denominator, ~monotonic toward the goal —
        // the "value of reaching this step" curve. Backend may also provide it pre-derived.
        const reachConversionRate = data.conversion_rate_reached ?? (
          totalSessions > 0 ? (conversionsReached / totalSessions) * 100 : 0
        )

        // 3. Progression Rate: Use the calculated rate from enhanced analytics
        const progressionRate = data.progression_rate ?? 0

        // Drop-off rate: Use the calculated rate from enhanced analytics
        const dropOffRate = data.drop_off_rate ?? 0

        // Check if this is a conversion step
        const isConversionStep = data.is_conversion === true

        // Use step name from enhanced analytics data
        const stepName = data.step_name || `Paso ${stepId}`

        return {
          name: stepName,
          users: totalSessions,  // Now represents sessions, not unique users
          usersEntered: sessionsEntered,  // Now represents sessions entered
          conversionsFromEntry: conversionsFromEntry,
          hits: data.total_events ?? 0,
          entryShare: Number(entryShare.toFixed(1)),
          entryConversionRate: Number(entryConversionRate.toFixed(1)),
          reachConversionRate: Number(reachConversionRate.toFixed(1)),
          progressionRate: Number(progressionRate.toFixed(1)),
          dropOffRate: Number(dropOffRate.toFixed(1)),
          stepId: stepId,
          stepNumber: data.step_order ?? (index + 1),
          isConversion: isConversionStep,
          stepType: data.step_type ?? 'funnel_step',
          isLastStep: false, // Recalculated after sort
          // Include entry sources for multi-entry analysis
          entry_sources: data.entry_sources,
          sessions_entered_here: sessionsEntered,
          sessions_progressed: data.sessions_progressed,
          sessions_dropped: data.sessions_dropped
        }
      })
      .sort((a, b) => a.stepNumber - b.stepNumber)
      .map((step, i, arr) => ({ ...step, isLastStep: i === arr.length - 1 }))
    : []



  const loadStoreFunnel = async () => {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) return
    setLoading(true)
    try {
      const data = await funnelService.getStoreFunnelAnalytics(storeId, windowDays)
      setFunnelData(data)
    } catch (error) {
      console.error('Error loading store funnel:', error)
      setFunnelData(null)
    } finally {
      setLoading(false)
    }
  }

  const loadFunnelConfig = async () => {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) return

    try {
      const config = await funnelService.getFunnelConfig(storeId)
      setFunnelConfig(config)
    } catch (error) {
      console.error('Error loading funnel configuration:', error)
      setFunnelConfig(null)
    }
  }

  // Sync initialFunnelData prop to state when it arrives
  useEffect(() => {
    if (initialFunnelData) {
      setFunnelData(initialFunnelData)
      setLoading(false)
    }
  }, [initialFunnelData])

  useEffect(() => {
    loadFunnelConfig()
    // Only fetch if no initialFunnelData prop was passed (undefined means "fetch yourself")
    // If initialFunnelData is explicitly passed (even as null), parent is handling the fetch
    if (initialFunnelData === undefined) {
      loadStoreFunnel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch per-day products + origins timeline once per (store, window).
  // Shared between ProductPerformanceTimeline and TrafficSourceTimeline.
  useEffect(() => {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) return
    let cancelled = false
    setTimelineData(null)
    funnelService
      .getStoreProductsOriginsTimeline(storeId, windowDays)
      .then((res) => {
        if (!cancelled) setTimelineData(res)
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load products/origins timeline:', err)
      })
    return () => {
      cancelled = true
    }
  }, [windowDays])


  const getStepColor = (index: number) => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444']
    return colors[index % colors.length]
  }

  // Clean window-aggregate traffic origins (one-per-session, paid/organic-aware),
  // summed across the per-day timeline series. Single source of truth for the
  // TrafficSourceTable and the acquisition-source insight below — equivalent to
  // aggregateOrigins over the window, replacing the old per-step entry_sources sum.
  const originsAggregate: Record<string, { sessions: number; conversions: number; revenue: number }> = {}
  // GA4-style channel rollup (P1): channels, GA4 reference totals, and origin coverage,
  // each summed across the per-day series → the window aggregate the table renders.
  const channelsAggregate: Record<string, { sessions: number; conversions: number; sessions_with_origin: number; revenue: number }> = {}
  const coverageAggregate = { sessions_total: 0, sessions_with_origin: 0 }
  // Device dimension (mobile/desktop/unknown), summed across the window — same pattern as origins.
  // `revenue` is 0 on snapshots predating Phase 2 (per-dimension revenue).
  const devicesAggregate: Record<string, { sessions: number; conversions: number; revenue: number }> = {}
  for (const point of timelineData?.series || []) {
    for (const [src, cell] of Object.entries(point.origins || {})) {
      if (!originsAggregate[src]) originsAggregate[src] = { sessions: 0, conversions: 0, revenue: 0 }
      originsAggregate[src].sessions += (cell as { sessions?: number })?.sessions || 0
      originsAggregate[src].conversions += (cell as { conversions?: number })?.conversions || 0
      originsAggregate[src].revenue += (cell as { revenue?: number })?.revenue || 0
    }
    for (const [dev, cell] of Object.entries(point.devices || {})) {
      if (!devicesAggregate[dev]) devicesAggregate[dev] = { sessions: 0, conversions: 0, revenue: 0 }
      devicesAggregate[dev].sessions += (cell as { sessions?: number })?.sessions || 0
      devicesAggregate[dev].conversions += (cell as { conversions?: number })?.conversions || 0
      devicesAggregate[dev].revenue += (cell as { revenue?: number })?.revenue || 0
    }
    for (const [ch, cell] of Object.entries(point.channels || {})) {
      if (!channelsAggregate[ch]) channelsAggregate[ch] = { sessions: 0, conversions: 0, sessions_with_origin: 0, revenue: 0 }
      channelsAggregate[ch].sessions += (cell as { sessions?: number })?.sessions || 0
      channelsAggregate[ch].conversions += (cell as { conversions?: number })?.conversions || 0
      channelsAggregate[ch].sessions_with_origin += (cell as { sessions_with_origin?: number })?.sessions_with_origin || 0
      channelsAggregate[ch].revenue += (cell as { revenue?: number })?.revenue || 0
    }
    const cov = point.origins_coverage
    if (cov) {
      coverageAggregate.sessions_total += cov.sessions_total || 0
      coverageAggregate.sessions_with_origin += cov.sessions_with_origin || 0
    }
  }
  // GA4 totals are a fixed rolling 7-day total → take the window-level value (latest
  // snapshot), NOT a per-day sum (which inflated by the snapshot count).
  const ga4ChannelTotals = timelineData?.ga4_channel_totals || null
  const hasGA4Channels = !!ga4ChannelTotals && Object.keys(ga4ChannelTotals).length > 0

  const getInsights = () => {
    if (!chartData.length) return []

    const insights: Array<{
      type: 'warning' | 'info' | 'success' | 'opportunity'
      title: string
      description: string
      action?: string
    }> = []

    // Get product analytics if available
    const productAnalytics = (funnelData as any)?.tnx_analytics?.product_analytics
      ?? (funnelData as any)?.product_analytics
    const topProducts = productAnalytics?.product_performance?.top_products || []

    // Traffic-weighted average for comparison (see productInsights.ts). An
    // unweighted mean of per-product rates lets tiny-sample products inflate the
    // baseline above every real product; weighting by visitors prevents that.
    const avgProductConversion = weightedAvgConversion(topProducts)

    // 1. PRODUCT-LEVEL: Find specific products underperforming vs average
    if (topProducts.length >= 3) {
      const productsWithEnoughViews = topProducts.filter((p: any) => p.views >= 10)

      // Best performing product vs average. Require the SAME robust sample as
      // the worst (>= 20 visitors) so the comparison anchor isn't a small-sample
      // outlier — an ~11-view product showing "52.2%" made laggards look absurdly
      // bad and contradicted the store's real (much lower) conversion rate.
      const bestProduct = productsWithEnoughViews
        .filter((p: any) => visitorsOf(p) >= 20)
        .sort((a: any, b: any) => (b.conversion_rate || 0) - (a.conversion_rate || 0))[0]

      // Worst performing product with enough traffic
      const worstProduct = productsWithEnoughViews
        .filter((p: any) => p.views >= 20)
        .sort((a: any, b: any) => (a.conversion_rate || 0) - (b.conversion_rate || 0))[0]

      // Require enough data on the laggard before making a call: a handful of
      // visitors can show any rate by chance. Gate on visitor volume, only fire
      // when there is real headroom vs the baseline, and project uplift over the
      // SAME denominator the rate uses (unique visitors) so the numbers in the
      // sentence reconcile (rate = sales / visitors).
      const MIN_VISITORS_FOR_INSIGHT = 30
      const worstVisitors = worstProduct ? visitorsOf(worstProduct) : 0

      if (
        bestProduct && worstProduct &&
        bestProduct.product_id !== worstProduct.product_id &&
        worstVisitors >= MIN_VISITORS_FOR_INSIGHT &&
        avgProductConversion > (worstProduct.conversion_rate || 0)
      ) {
        const improvement = bestProduct.conversion_rate - worstProduct.conversion_rate

        if (improvement > 1) { // More than 1% difference
          const uplift = avgProductConversion - (worstProduct.conversion_rate || 0)
          const additionalSales = Math.round(worstVisitors * uplift / 100)
          const worstConversions = worstProduct.conversions || 0

          if (additionalSales >= 1) {
            insights.push({
              type: 'opportunity',
              title: `"${worstProduct.product_name}" convierte ${worstProduct.conversion_rate?.toFixed(1) || 0}% vs ${bestProduct.conversion_rate?.toFixed(1)}% de "${bestProduct.product_name}"`,
              description: `${worstVisitors} visitantes → ${worstConversions} ${worstConversions === 1 ? 'venta' : 'ventas'} (${(worstProduct.conversion_rate || 0).toFixed(1)}%). Si alcanzara el promedio entre fichas de producto (${avgProductConversion.toFixed(1)}%), ganarías ~${additionalSales} ${additionalSales === 1 ? 'venta' : 'ventas'} adicionales.`,
              action: `Revisa la ficha de "${worstProduct.product_name}": imágenes, precio, descripción. Considera un A/B test.`
            })
          }
        }
      }

      // High-traffic product below average
      const highTrafficUnderperformer = productsWithEnoughViews
        .filter((p: any) => p.views >= 50 && (p.conversion_rate || 0) < avgProductConversion * 0.7)
        .sort((a: any, b: any) => b.views - a.views)[0]

      if (highTrafficUnderperformer && !insights.some(i => i.title.includes(highTrafficUnderperformer.product_name))) {
        insights.push({
          type: 'warning',
          title: `"${highTrafficUnderperformer.product_name}": ${highTrafficUnderperformer.views} visitas pero solo ${highTrafficUnderperformer.conversion_rate?.toFixed(1)}% conversión`,
          description: `Este producto recibe mucho tráfico pero convierte ${((avgProductConversion - highTrafficUnderperformer.conversion_rate) / avgProductConversion * 100).toFixed(0)}% menos que el promedio entre fichas de producto (${avgProductConversion.toFixed(1)}%).`,
          action: 'Alto impacto potencial: mejora esta ficha de producto o revisa si el precio es competitivo.'
        })
      }
    }

    // 2. ENTRY SOURCE: Compare conversion by traffic source category
    const sourceConversions: Record<string, { sessions: number, conversions: number }> = {}

    chartData.forEach(step => {
      if (!step.entry_sources) return

      const int = step.entry_sources.internal || {}

      // Internal navigation paths only — these feed the Categorías-vs-Búsqueda
      // comparison below. External acquisition sources (Google/Facebook/Directo)
      // now come from the clean one-per-session origins (acqSources), not this
      // per-step entry_sources estimate, so they're no longer aggregated here.
      const sources = [
        { name: 'Categorías', count: int.category || 0 },
        { name: 'Búsqueda interna', count: int.search || 0 },
      ]

      sources.forEach(src => {
        if (src.count > 0) {
          if (!sourceConversions[src.name]) {
            sourceConversions[src.name] = { sessions: 0, conversions: 0 }
          }
          sourceConversions[src.name].sessions += src.count
          // Estimate conversions proportionally based on entry conversion rate
          sourceConversions[src.name].conversions += Math.round(src.count * step.entryConversionRate / 100)
        }
      })
    })

    // Acquisition sources (external only) from the clean one-per-session origins.
    // Internal navigation (Categorías/Búsqueda/Homepage) is compared separately below
    // from entry_sources — it's a navigation path, not a traffic origin.
    const oa = (k: string) => originsAggregate[k] || { sessions: 0, conversions: 0 }
    const acqSources = [
      { name: 'Google', sessions: oa('google').sessions + oa('google_paid').sessions, conversions: oa('google').conversions + oa('google_paid').conversions },
      { name: 'Facebook/Meta', sessions: oa('facebook').sessions + oa('instagram').sessions + oa('meta_ads').sessions, conversions: oa('facebook').conversions + oa('instagram').conversions + oa('meta_ads').conversions },
      { name: 'Directo', sessions: oa('direct').sessions, conversions: oa('direct').conversions },
      { name: 'Otros pagados', sessions: oa('other_paid').sessions, conversions: oa('other_paid').conversions },
      // Note: the generic "Otros" (other_search + other_external) catch-all is
      // intentionally excluded — it's not a channel you can buy or steer, so
      // recommending "redistribute budget toward Otros" is non-actionable.
    ]

    // Find best and worst performing sources
    const sourcesWithData = acqSources
      .filter((data) => data.sessions >= 10)
      .map((data) => ({
        name: data.name,
        sessions: data.sessions,
        conversions: data.conversions,
        rate: data.sessions > 0 ? (data.conversions / data.sessions) * 100 : 0
      }))
      .sort((a, b) => b.rate - a.rate)

    if (sourcesWithData.length >= 2) {
      const best = sourcesWithData[0]
      const worst = sourcesWithData[sourcesWithData.length - 1]

      // Origin conversions are REAL now (one-per-session, paid/organic-aware),
      // not estimated. Still require a meaningful number of conversions on the
      // worst source before claiming an "Nx" gap — a single conversion on a few
      // hundred sessions is noise, and it guarantees worst.rate > 0 so the
      // multiplier stays finite.
      const MIN_CONVERSIONS_FOR_SOURCE_INSIGHT = 5
      if (
        best.rate > worst.rate * 1.5 &&
        worst.conversions >= MIN_CONVERSIONS_FOR_SOURCE_INSIGHT &&
        worst.sessions >= 20
      ) {
        const multiplier = best.rate / worst.rate
        insights.push({
          type: 'info',
          title: `${best.name} convierte ${best.rate.toFixed(1)}% vs ${worst.rate.toFixed(1)}% de ${worst.name}`,
          description: `De ${worst.sessions} sesiones de ${worst.name}, ${worst.conversions} ${worst.conversions === 1 ? 'convirtió' : 'convirtieron'}. ${best.name} es ${multiplier.toFixed(1)}x más efectivo.`,
          action: worst.name.includes('Facebook') || worst.name.includes('Meta')
            ? `Revisa la segmentación de tus campañas de ${worst.name} o mejora las landing pages de esas campañas.`
            : `Considera redistribuir presupuesto hacia ${best.name} o mejorar la experiencia de ${worst.name}.`
        })
      }
    }

    // 3. Category vs Search: navigation path comparison
    const categorySource = sourceConversions['Categorías']
    const searchSource = sourceConversions['Búsqueda interna']

    if (categorySource?.sessions >= 20 && searchSource?.sessions >= 20) {
      const catRate = (categorySource.conversions / categorySource.sessions) * 100
      const searchRate = (searchSource.conversions / searchSource.sessions) * 100

      if (Math.abs(catRate - searchRate) > 1) {
        const better = catRate > searchRate ? 'Categorías' : 'Búsqueda'
        const worse = catRate > searchRate ? 'Búsqueda' : 'Categorías'
        const betterRate = Math.max(catRate, searchRate)
        const worseRate = Math.min(catRate, searchRate)

        insights.push({
          type: 'info',
          title: `Sesiones de ${better} convierten ${betterRate.toFixed(1)}% vs ${worseRate.toFixed(1)}% de ${worse}`,
          description: better === 'Categorías'
            ? 'Las sesiones desde categorías tienen mejor intent de compra.'
            : 'Las sesiones desde búsqueda tienen mayor intent. Tu buscador funciona bien.',
          action: better === 'Categorías'
            ? 'Mejora la relevancia de resultados de búsqueda o añade filtros más específicos.'
            : 'Considera mejorar la navegación por categorías con filtros y ordenamiento.'
        })
      }
    }

    // 4. Conversion step specific insight
    const conversionStep = chartData.find(s => s.isConversion)
    const preConversionStep = chartData.find(s => s.stepNumber === (conversionStep?.stepNumber || 0) - 1)

    // Min sessions before calling a checkout step a "critical bottleneck" — a
    // handful of sessions is noise, not a funnel problem.
    const MIN_SESSIONS_FOR_CHECKOUT_INSIGHT = 20

    if (
      conversionStep && preConversionStep &&
      preConversionStep.progressionRate < 30 &&
      (preConversionStep.users || 0) >= MIN_SESSIONS_FOR_CHECKOUT_INSIGHT
    ) {
      // Drop-off must come from THIS step's own progression data, not from
      // subtracting the conversion step's session count. Funnel steps aren't
      // nested subsets (sessions can reach purchase via other paths), so
      // cross-step subtraction can go negative (the "-19 no completan" bug).
      const sessions = preConversionStep.users || 0
      const notCompleted = stepNonCompleters(preConversionStep)

      insights.push({
        type: 'warning',
        title: `Solo ${preConversionStep.progressionRate}% completa la compra desde "${preConversionStep.name}"`,
        description: `De ${sessions} sesiones en "${preConversionStep.name}", ${notCompleted} no completan. Este es tu cuello de botella crítico.`,
        action: 'Revisa el proceso de checkout: costos de envío, métodos de pago, formularios largos.'
      })
    }

    // 5. Overall health (only if no other insights)
    if (insights.length === 0) {
      // Denominator MUST match the headline "Tasa de conversión" shown in the
      // same card (store-wide unique sessions), not the max funnel step. The
      // max step only counts sessions that reached the funnel (~293), so it
      // reported 24/293 = 8.2% next to the headline's 24/600 = 4.00% — two
      // contradictory conversion rates in one card.
      const totalUsers = (funnelData as any)?.tnx_analytics?.unique_sessions || 0
      const conversions = conversionStep?.users || 0
      const completionRate = totalUsers > 0 ? (conversions / totalUsers) * 100 : 0

      if (completionRate >= 3) {
        insights.push({
          type: 'success',
          title: `${conversions} conversiones de ${totalUsers} sesiones (${completionRate.toFixed(1)}%)`,
          description: 'Tu funnel supera el benchmark de e-commerce (2-3%). Buen trabajo.',
        })
      } else if (totalUsers > 50 && completionRate < 1) {
        insights.push({
          type: 'warning',
          title: `Tasa de conversión baja: ${completionRate.toFixed(2)}%`,
          description: `${conversions} de ${totalUsers} sesiones. El benchmark de e-commerce es 2-3%.`,
          action: 'Analiza los productos más visitados y mejora sus fichas.'
        })
      }
    }

    return insights
  }

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex items-center justify-between gap-4">
          <div className="animate-pulse">
            <div className="h-8 w-48 rounded bg-muted mb-2"></div>
            <div className="h-4 w-96 rounded bg-muted"></div>
          </div>
          <div className="flex gap-3 animate-pulse">
            <div className="h-10 w-48 rounded bg-muted"></div>
            <div className="h-10 w-48 rounded bg-muted"></div>
          </div>
        </div>

        {/* Metrics grid skeleton */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <div className="animate-pulse space-y-3">
                <div className="h-4 w-24 rounded bg-muted"></div>
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 rounded bg-muted"></div>
                  <div className="h-8 w-16 rounded bg-muted"></div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Chart skeleton */}
        <Card>
          <div className="animate-pulse space-y-2">
            <div className="h-6 w-40 rounded bg-muted mb-2"></div>
            <div className="h-4 w-64 rounded bg-muted"></div>
            <div className="h-96 rounded bg-muted"></div>
          </div>
        </Card>

        {/* Table skeleton */}
        <Card>
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-32 rounded bg-muted"></div>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center justify-between p-4 border rounded">
                <div className="flex items-center gap-3">
                  <div className="h-6 w-6 rounded bg-muted"></div>
                  <div className="h-4 w-32 rounded bg-muted"></div>
                </div>
                <div className="flex gap-8">
                  <div className="h-4 w-16 rounded bg-muted"></div>
                  <div className="h-4 w-16 rounded bg-muted"></div>
                  <div className="h-4 w-16 rounded bg-muted"></div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    )
  }

  if (!funnelData) {
    return (
      <Card>
        <div className="py-12 text-center">
          <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-muted">
            <StatsIcon size={48} />
          </div>
          <Title as="h3" className="mb-2">
            No hay datos de funnel disponibles
          </Title>
          <Text>
            Los datos del funnel aparecerán cuando los visitantes interactúen
            con tu tienda.
          </Text>
        </div>
      </Card>
    )
  }

  // Check if we have enhanced analytics with funnel step data
  const hasEnhancedAnalytics = funnelData && (
    funnelData.tnx_analytics?.step_analysis ||
    funnelData.step_analysis ||
    funnelData.steps
  )

  const hasGeneralAnalytics = funnelData && (
    funnelData.tnx_analytics ||
    funnelData.total_events ||
    funnelData.unique_visitors
  )

  // Check if we have meaningful step data (not all zeros)
  const hasMeaningfulSteps = chartData.length > 0 && chartData.some(step => step.users > 0 || step.hits > 0)

  // If we have general analytics but no enhanced funnel step data, show a summary view
  if (hasGeneralAnalytics && !hasEnhancedAnalytics) {
    const analytics = funnelData.tnx_analytics || funnelData

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <Title as="h2">Análisis de Tienda</Title>
            <Text>
              Métricas generales de tu tienda (análisis de funnel en desarrollo)
            </Text>
          </div>
        </div>

        {/* General Analytics Summary */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <Card>
            <Card.Header title="Total de Eventos" />
            <Card.Body>
              <div className="flex items-center gap-2">
                <StatsIcon />
                <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">
                  {(analytics.total_events || 0).toLocaleString()}
                </Text>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header title="Sesiones Únicas" />
            <Card.Body>
              <div className="flex items-center gap-2">
                <UserGroupIcon />
                <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">
                  {(analytics.unique_sessions || 0).toLocaleString()}
                </Text>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header title="Experimentos Activos" />
            <Card.Body>
              <div className="flex items-center gap-2">
                <FlagIcon />
                <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">
                  {(analytics.active_experiments || []).length}
                </Text>
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Popular Pages */}
        {analytics.popular_pages && analytics.popular_pages.length > 0 && (
          <Card>
            <Card.Header>
              <Title as="h3">Páginas Más Visitadas</Title>
              <Text>
                Las páginas con mayor tráfico en el período seleccionado
              </Text>
            </Card.Header>
            <Card.Body>
              <div className="space-y-3">
                {analytics.popular_pages.slice(0, 5).map((page: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg border">
                    <div className="flex-1">
                      <Text fontWeight="medium" color="neutral-textHigh">{page.page}</Text>
                      <Text fontSize="caption">
                        {page.views} visualizaciones
                      </Text>
                    </div>
                    <div className="text-right">
                      <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{page.views}</Text>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Event Types */}
        {analytics.event_types && Object.keys(analytics.event_types).length > 0 && (
          <Card>
            <Card.Header>
              <Title as="h3">Distribución de Eventos</Title>
              <Text>
                Tipos de eventos registrados por los visitantes
              </Text>
            </Card.Header>
            <Card.Body>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {Object.entries(analytics.event_types).map(([type, count]: [string, any]) => (
                  <div key={type} className="p-3 rounded-lg border text-center">
                    <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{count}</Text>
                    <div className="capitalize">
                      <Text fontSize="caption">{type.replace(/_/g, ' ')}</Text>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Info about funnel analysis */}
        <Card>
          <Card.Body>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-blue-50 border border-blue-200">
              <span className="text-blue-600"><Icon source={<ExclamationTriangleIcon />} color="currentColor" /></span>
              <div>
                <div className="font-medium text-blue-900">
                  Análisis de funnel en desarrollo
                </div>
                <div className="text-sm text-blue-700">
                  El análisis detallado de progresión entre pasos del funnel estará disponible próximamente.
                  Los datos mostrados representan las métricas generales de tu tienda.
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Data Quality Warnings */}
      {funnelData && (
        <>


          {/* Check entry point coverage */}
          {(funnelData as any).data_quality?.entry_coverage < 0.9 && (
            <Alert appearance="warning" title="Cobertura de puntos de entrada baja">
              Solo se puede identificar el punto de entrada para el{' '}
              {((funnelData as any).data_quality?.entry_coverage * 100).toFixed(0)}% de los visitantes.
              Algunas sesiones pueden estar incompletas.
            </Alert>
          )}
        </>
      )}

      {/* Top summary band — 2 columns on desktop:
          [Análisis de Funnel + Oportunidades in one card] | [Segmentación de Visitantes]
          items-stretch + h-full make both columns share the taller column's height. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-stretch">
        <Card>
          <div className="flex h-full flex-col">
            <Card.Header>
              <Title as="h3">Análisis de Funnel de Tienda</Title>
              <Text>Métricas clave del periodo</Text>
            </Card.Header>
            <Card.Body>
              <div className="flex flex-1 flex-col space-y-4">
                {/* Funnel overview metrics */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserGroupIcon />
                    <Text>Sesiones totales</Text>
                  </div>
                  <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">
                    {((funnelData as any)?.tnx_analytics?.unique_sessions || 0).toLocaleString()}
                  </Text>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600"><Icon source={<FlagIcon />} color="currentColor" /></span>
                    <Text>Conversiones</Text>
                  </div>
                  <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">
                    {getConversionCount().toLocaleString()}
                  </Text>
                </div>
                <div className="flex items-center justify-between border-t pt-4">
                  <div className="flex items-center gap-2">
                    <span className="text-blue-600"><Icon source={<CheckCircleIcon />} color="currentColor" /></span>
                    <Text fontWeight="medium" color="neutral-textHigh">Tasa de conversión</Text>
                  </div>
                  <span className="text-2xl font-bold tabular-nums text-blue-700">
                    {(() => {
                      const totalSessions = (funnelData as any)?.tnx_analytics?.unique_sessions || 0
                      const conversions = getConversionCount()
                      if (totalSessions === 0) return 'N/A'
                      const rate = (conversions / totalSessions) * 100
                      return rate.toFixed(2) + '%'
                    })()}
                  </span>
                </div>
                <Text fontSize="caption">
                  Conversiones / Sesiones totales
                </Text>

                {/* Opportunities — rendered bare, scrolls if it overflows the matched height. */}
                <div className="mt-2 flex-1 overflow-y-auto border-t pt-4">
                  <OptimizationOpportunities
                    insights={getInsights()}
                    chartData={chartData}
                    bare
                  />
                </div>
              </div>
            </Card.Body>
          </div>
        </Card>

        <VisitorSegmentation
          segments={(funnelData as any)?.tnx_analytics?.visitor_segments}
        />
      </div>

      {/* Multi-Entry Funnel Analysis */}
      <div className="space-y-6">

      {/* Main content: Version A (Sankey) or Version B (Tables) */}
      {dashboardVersion === 'A' ? (
        /* VERSION A: Sankey + Insights sidebar */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sankey flow diagram - 2 columns */}
          <div className="lg:col-span-2">
            <Card>
              <Card.Header>
                <Title as="h3">Flujo Multi-Entrada del Funnel</Title>
                <Text>
                  Visualización de puntos de entrada y progresión entre pasos del funnel
                </Text>
              </Card.Header>
              <Card.Body>
                <D3SankeyChart funnelData={funnelData} chartData={chartData} funnelConfig={funnelConfig} />

                {/* Enhanced description */}
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Cómo leer este diagrama:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• <strong>Ancho de las conexiones:</strong> Representa el número de sesiones que fluyen entre pasos</li>
                    <li>• <strong>Múltiples entradas:</strong> Las sesiones pueden comenzar en cualquier paso del funnel</li>
                    <li>• <strong>Números en nodos:</strong> Muestran total de sesiones en cada paso</li>
                    <li>• <strong>Porcentajes:</strong> Indican participación de entrada y progresión entre pasos</li>
                  </ul>
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* Insights sidebar - 1 column */}
          <div className="space-y-4">
            <Card>
              <Card.Header>
                <Title as="h4">Insights y Oportunidades</Title>
                <Text>
                  Análisis automático con recomendaciones accionables
                </Text>
              </Card.Header>
              <Card.Body>
                <div className="space-y-3">
                  {getInsights().length === 0 ? (
                    <div className="text-center py-4">
                      <Text>Se necesitan más datos para generar insights.</Text>
                    </div>
                  ) : (
                    getInsights().map((insight, index) => (
                      <Alert
                        key={index}
                        title={insight.title}
                        appearance={
                          insight.type === 'warning' ? 'danger'
                            : insight.type === 'opportunity' ? 'warning'
                            : insight.type === 'success' ? 'success'
                            : 'neutral'
                        }
                      >
                        <p className="text-xs">
                          {insight.description}
                        </p>
                        {insight.action && (
                          <p className="text-xs mt-2 font-medium">
                            → {insight.action}
                          </p>
                        )}
                      </Alert>
                    ))
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      ) : (
        /* VERSION B: Actionable Tables — table + per-day timeline side-by-side on lg+ */
        <div className="space-y-6">
          {/* Revenue headline (consultant tier — web revenue / AOV / revenue-per-session). */}
          <RevenueAnalyticsCard
            analytics={(funnelData as any)?.tnx_analytics}
            detailed
          />
          {/* P3 — per-day revenue trend (revenue / AOV / RPS toggle), shares the timeline fetch. */}
          <RevenueTrendChart
            storeId={authService.getCurrentStoreId() || ''}
            windowDays={windowDays}
            initialData={timelineData}
          />
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <TrafficSourceTable
              channels={channelsAggregate}
              ga4ChannelTotals={hasGA4Channels ? ga4ChannelTotals : null}
              coverage={coverageAggregate.sessions_total > 0 ? coverageAggregate : null}
              breakdown={timelineData?.traffic_origins_breakdown || null}
            />
            <TrafficSourceTimeline
              storeId={authService.getCurrentStoreId() || ''}
              windowDays={windowDays}
              initialData={timelineData}
            />
          </div>
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <BreakdownTable
              title="Conversión por dispositivo"
              rows={Object.entries(devicesAggregate).map(([key, c]) => ({
                key,
                label: key === 'mobile' ? 'Celular' : key === 'desktop' ? 'Escritorio' : 'Sin dato',
                sessions: c.sessions,
                conversions: c.conversions,
                revenue: c.revenue,
              }))}
              note="«Sin dato» = sesiones sin viewport detectado (no es un segmento accionable)."
            />
            {/* Off-storefront orders excluded from the metrics above — consultant tier (detailed). */}
            <OffStorefrontCard
              off={(funnelData as any)?.tnx_analytics?.off_storefront_orders}
              summary={(funnelData as any)?.tnx_analytics?.storefront_summary}
              detailed
            />
            {/* On-site search (INS-1): usage, searcher-vs-non CR (with intent-bias caveat), top terms. */}
            <SearchAnalyticsCard search={(funnelData as any)?.tnx_analytics?.search_analytics} />
          </div>
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <ProductPerformanceTable
              tnxProducts={
                (funnelData as any)?.tnx_analytics?.product_analytics?.product_performance?.top_products
                ?? (funnelData as any)?.product_analytics?.product_performance?.top_products
              }
              ga4Products={(funnelData as any)?.ga4_analytics?.product_analytics}
            />
            <ProductPerformanceTimeline
              storeId={authService.getCurrentStoreId() || ''}
              windowDays={windowDays}
              initialData={timelineData}
            />
          </div>
        </div>
      )}


      {/* Funnel metrics table */}
      <Card>
        <Card.Header>
          <Title as="h3">Métricas del Funnel por Paso</Title>
          <Text>
            Para cada paso: cuántas sesiones lo alcanzan, qué porcentaje termina convirtiendo, cuánto
            multiplica eso la conversión frente al paso anterior, y qué porcentaje avanza más en el funnel
          </Text>
        </Card.Header>
        <Card.Body>
          {hasMeaningfulSteps ? (
            <Table minWidth="720px" stickyScrollbar>
              <Table.Head>
                <Table.Row>
                  <Table.Cell as="th">Paso</Table.Cell>
                  <Table.Cell as="th" className="text-center">Tráfico (alcance)</Table.Cell>
                  <Table.Cell as="th" className="text-center">Conversión a meta</Table.Cell>
                  <Table.Cell as="th" className="text-center">Conversión-lift vs. paso previo</Table.Cell>
                  <Table.Cell as="th" className="text-center">Avanza en el funnel</Table.Cell>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {chartData.map((step, index) => (
                  <Table.Row key={step.stepId}>
                    <Table.Cell>
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-flex items-center justify-center rounded border px-2 py-0.5 text-xs font-medium"
                          style={{
                            backgroundColor: getStepColor(index) + '20',
                            borderColor: getStepColor(index),
                          }}
                        >
                          {step.stepNumber}
                        </span>
                        <Text as="span" fontWeight="medium" color="neutral-textHigh">{step.name}</Text>
                        {step.isConversion && (
                          <div className="shrink-0 whitespace-nowrap ml-2">
                            <Tag appearance="primary">Conversión</Tag>
                          </div>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="text-center">
                      <div className="space-y-1">
                        <div className="font-mono font-semibold">
                          {step.users.toLocaleString()}
                        </div>
                        <Text fontSize="caption">
                          sesiones · {step.usersEntered.toLocaleString()} entraron aquí ({step.entryShare}%)
                        </Text>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="text-center">
                      {step.users > 0 ? (
                        <div className="space-y-1">
                          {/* No trend arrow: this rate rises monotonically toward the goal by
                              construction, so a per-step up/down marker carries no signal. The
                              actionable comparison lives in the "Conversión-lift" column. */}
                          <span className="font-mono font-semibold tabular-nums">
                            {step.reachConversionRate}%
                          </span>
                          <Text fontSize="caption">
                            convierten al alcanzarlo
                          </Text>
                        </div>
                      ) : (
                        <Text as="span">-</Text>
                      )}
                    </Table.Cell>
                    <Table.Cell className="text-center">
                      {(() => {
                        // "Value of the step": how much reaching it multiplies conversion vs the
                        // previous step. Multiplicative read is the actionable CRO signal.
                        const prev = index > 0 ? chartData[index - 1] : null
                        if (!prev || prev.reachConversionRate <= 0 || step.reachConversionRate <= 0) {
                          return <Text as="span">-</Text>
                        }
                        const mult = step.reachConversionRate / prev.reachConversionRate
                        const isUp = mult >= 1
                        return (
                          <div className={`flex items-center justify-center gap-1 font-mono ${
                            mult >= 1.5 ? 'text-green-600' : mult >= 0.9 ? 'text-gray-500' : 'text-red-600'
                          }`}>
                            {isUp ? (
                              <Icon source={<ArrowupIcon />} color="currentColor" />
                            ) : (
                              <Icon source={<ArrowDownIcon />} color="currentColor" />
                            )}
                            <span>{mult.toFixed(1)}×</span>
                          </div>
                        )
                      })()}
                    </Table.Cell>
                    <Table.Cell className="text-center">
                      {step.isLastStep ? (
                        <Text as="span">
                          {step.isConversion ? '✓ Meta' : '-'}
                        </Text>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          {step.progressionRate >= 70 ? (
                            <span className="text-green-600"><Icon source={<ArrowupIcon />} color="currentColor" /></span>
                          ) : step.progressionRate >= 40 ? (
                            <span className="text-yellow-600"><Icon source={<ArrowupIcon />} color="currentColor" /></span>
                          ) : (
                            <span className="text-red-600"><Icon source={<ArrowDownIcon />} color="currentColor" /></span>
                          )}
                          <span className="font-mono">
                            {step.progressionRate}%
                          </span>
                        </div>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <StatsIcon size={32} />
              </div>
              <Title as="h3" className="mb-2">
                Funnel configurado, esperando tráfico
              </Title>
              <Text>
                El funnel está configurado con {chartData.length} pasos. Los datos aparecerán cuando los visitantes
                interactúen con tu tienda siguiendo el flujo del funnel.
              </Text>
              {chartData.length > 0 && (
                <div className="mt-4">
                  <Text fontSize="caption">
                    <strong>Pasos configurados:</strong> {chartData.map(step => step.name).join(' → ')}
                  </Text>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      </div>
    </div>
  )
}
